import { create } from 'zustand';
import { authService } from '@/services/AuthService';
import { queryClient } from '@/lib/queryClient';
import { parseJwtClaims, getTokenExpiration } from '@/utils/jwtUtils';
import { triggerJitProvisioning } from '@/utils/jitProvision';
import { API_CONFIG } from '@/config/api';
import { AUTH_BFF_ENABLED } from '@/config/authBff';
import type { AuthState, AuthActions, TokenResponse, User } from '@/types/Auth';
import { filterEquipRoles } from '@equip/utils';
import { logger } from '@equip/utils/logger';
import { telemetry } from '@/utils/telemetry';

/**
 * Thrown when an authenticated identity carries no recognised eQuip application
 * role (user/admin/viewer/frontwindow). The token is otherwise valid, but the
 * user has not been granted access to eQuip, so the session must be rejected.
 */
export class AuthorizationError extends Error {
  constructor(message = 'Your account is not authorized to access eQuip.') {
    super(message);
    this.name = 'AuthorizationError';
  }
}

// Multi-Tab Session Limitation (Code Review Issue #CR1)
// Tokens are stored only in-memory (not in sessionStorage/localStorage).
// This means each browser tab requires separate login, which confuses users.
// Single-tab token refresh uses module-level promise which is not shared across tabs.
// Multiple tabs refreshing simultaneously can cause token revocation.
// Consider: BroadcastChannel API for cross-tab sync or sessionStorage with XSS mitigations.

type AuthStore = AuthState & AuthActions;
type TestWindow = Window & { __TEST_AUTH_TOKEN__?: unknown; useAuthStore?: unknown };

// Shared promise to prevent multiple simultaneous refresh requests
let refreshPromise: Promise<boolean> | null = null;
let refreshTimerId: ReturnType<typeof setTimeout> | null = null;

const scheduleTokenRefresh = (accessToken: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }

  const expiration = getTokenExpiration(accessToken);
  if (!expiration) {
    return;
  }

  const timeUntilRefresh = expiration.getTime() - Date.now() - API_CONFIG.TOKEN_REFRESH_BUFFER_MS;

  if (timeUntilRefresh <= 0) {
    refreshTimerId = null;
    void useAuthStore.getState().refreshTokens();
    return;
  }

  refreshTimerId = setTimeout(() => {
    refreshTimerId = null;
    void useAuthStore.getState().refreshTokens();
  }, timeUntilRefresh);
};

export const useAuthStore = create<AuthStore>((set, get) => {
  // Initialize state
  let initialState = {
    user: null as User | null,
    accessToken: null as string | null,
    idToken: null as string | null,
    refreshToken: null as string | null,
    isAuthenticated: false,
    isLoading: true, // Start as loading to prevent premature redirects during SSO attempt
    isDemoServiceMemberSession:
      typeof window !== 'undefined' && localStorage.getItem('demo_sm_session') === '1',
  };

  // Check for test auth token on store creation (E2E testing support)
  if (typeof window !== 'undefined') {
    const testToken = (window as TestWindow).__TEST_AUTH_TOKEN__;
    const testMode = sessionStorage.getItem('__test_mode__');

    if (typeof testToken === 'string' && testToken.length > 0 && testMode) {
      try {
        // Inject test tokens synchronously during store creation
        const user = parseJwtClaims(testToken);
        initialState = {
          user,
          accessToken: testToken,
          idToken: testToken,
          refreshToken: 'mock-refresh-token',
          isAuthenticated: true,
          isLoading: false,
          isDemoServiceMemberSession: false,
        };
        console.log('Test mode: Auth tokens injected during store creation');
      } catch (error) {
        console.error('Test mode: Failed to parse test token', error);
      }
    }
  }

  return {
    // State
    ...initialState,

    // Actions
    login: async () => {
      // Signing in as a regular user terminates any active demo service-member session.
      try {
        localStorage.removeItem('demo_sm_session');
      } catch {
        /* ignore */
      }
      set({ isLoading: true, isDemoServiceMemberSession: false });
      try {
        // Workforce BFF: delegate the OAuth flow to actions-api (server-side PKCE);
        // the browser is redirected to the BFF login endpoint.
        if (AUTH_BFF_ENABLED) {
          authService.startBffLogin();
          return;
        }
        // Assuming authService.login handles the redirect.
        // The rest of the login flow will be handled by a callback page.
        await authService.login();
      } catch (error) {
        logger.error('Login failed', { component: 'AuthStore', error: String(error) });
        set({ isLoading: false });
        throw error;
      }
    },

    /**
     * Non-production only: starts a PKCE login flow with a pre-filled username/email.
     * Marks the session as a demo service-member session so the auth callback injects
     * the service-member + user roles after sign-in, enabling UI testing without
     * requiring Workforce app role assignments.
     *
     * Signing in as a service member terminates any active regular user session first.
     */
    loginAsDemoServiceMember: async (
      person: {
        id: string;
        display_name: string | null;
        email: string;
        individual_id?: string | null;
      },
      password?: string,
    ) => {
      // Fetch a real signed HS256 JWT from the dev-only actions-api endpoint.
      // PostgREST accepts this token via the oct key injected into the JWKS by the Tiltfile.
      const body: Record<string, unknown> = {
        id: person.id,
        email: person.email,
        displayName: person.display_name,
      };
      if (password) {
        body.password = password;
      }
      const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEMO_SM_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(errData?.message ?? `demo-sm-token request failed: ${res.status}`);
      }
      const { token } = (await res.json()) as { token: string };

      const nameParts = (person.display_name ?? '').split(' ');
      const user: User = {
        id: person.id,
        username: person.email,
        email: person.email,
        firstName: nameParts[0] ?? '',
        lastName: nameParts.slice(1).join(' ') || undefined,
        roles: ['user', 'service-member'],
        individualId: person.individual_id ?? undefined,
        displayName: person.display_name ?? undefined,
      };

      try {
        localStorage.setItem('demo_sm_session', '1');
      } catch {
        /* ignore */
      }

      // Purge all previously cached per-user data (e.g. notifications) before
      // establishing the impersonated identity so no other user's cached
      // results can leak into this session (#1009).
      queryClient.clear();

      set({
        user,
        accessToken: token,
        idToken: token,
        refreshToken: 'demo-refresh-token',
        isAuthenticated: true,
        isLoading: false,
        isDemoServiceMemberSession: true,
      });
    },

    loginWithHint: async (hint: string) => {
      // Mark this as a demo service-member session before the PKCE redirect.
      try {
        localStorage.setItem('demo_sm_session', '1');
      } catch {
        /* ignore */
      }
      // Terminate any existing regular session (mutual exclusion).
      get().clearAuth();
      set({ isLoading: true, isDemoServiceMemberSession: true });
      try {
        await authService.login(false, hint);
      } catch (error) {
        logger.error('Demo service-member login failed', {
          component: 'AuthStore',
          error: String(error),
        });
        try {
          localStorage.removeItem('demo_sm_session');
        } catch {
          /* ignore */
        }
        set({ isLoading: false, isDemoServiceMemberSession: false });
        throw error;
      }
    },

    clearAuth: () => {
      if (refreshTimerId) {
        clearTimeout(refreshTimerId);
        refreshTimerId = null;
      }

      // Purge all previously cached per-user data (e.g. notifications) so it
      // cannot leak into the next session that authenticates in this tab (#1009).
      queryClient.clear();

      set({
        user: null,
        accessToken: null,
        idToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        isDemoServiceMemberSession: false,
      });
    },

    logout: ({ fullSignOut = false }: { fullSignOut?: boolean } = {}) => {
      get().clearAuth();
      // Clear PKCE / auth session artifacts so the next login starts clean.
      try {
        localStorage.removeItem('pkce_verifier');
        localStorage.removeItem('oauth_state');
        localStorage.removeItem('login_initiated');
        localStorage.removeItem('used_codes');
        // Clear demo service-member session flag on any sign-out.
        localStorage.removeItem('demo_sm_session');
        sessionStorage.removeItem('token_exchange_in_progress');
        sessionStorage.removeItem('auth_in_progress');
        // Suppress silent SSO so the login page stays visible after sign-out.
        // Without this the Workforce SSO session (still alive in local-only sign-out)
        // would immediately re-authenticate the user via the silent iframe.
        localStorage.setItem('silent_sso_last_fail', Date.now().toString());
      } catch {
        // Ignore storage errors.
      }
      if (AUTH_BFF_ENABLED) {
        // Clear the BFF httpOnly cookies server-side, then navigate. For a full
        // sign-out follow the Workforce end-session URL the BFF returns.
        void authService.logoutViaBff().then((endSessionUrl) => {
          window.location.href = fullSignOut && endSessionUrl ? endSessionUrl : '/login';
        });
        return;
      }
      if (fullSignOut) {
        // Full Workforce end-session: clears the Microsoft SSO session so the next
        // sign-in always prompts for credentials.  Use for production log-out.
        authService.logout();
      } else {
        // Local-only sign-out: clear in-app state and go to /login.  The Workforce
        // SSO session stays alive so the user can sign back in with one click.
        window.location.href = '/login';
      }
    },

    setTokens: async (tokens: TokenResponse) => {
      try {
        let user = parseJwtClaims(tokens.access_token);

        // Non-production demo: if the session was started via the "Sign in as Service
        // Member" button, inject service-member + user roles into the parsed user object.
        // This lets testers experience the service-member UI without requiring Workforce
        // app role assignments. The flag is set in loginWithHint() before the PKCE redirect.
        const isDemoSm =
          typeof window !== 'undefined' && localStorage.getItem('demo_sm_session') === '1';
        if (isDemoSm) {
          const demoRoles = ['user', 'service-member'];
          user = { ...user, roles: Array.from(new Set([...user.roles, ...demoRoles])) };
          logger.debug('Demo service-member: injected roles', {
            component: 'AuthStore',
            roles: user.roles,
          });
        }

        // Authorization gate: the token may be validly signed yet belong to an
        // identity that was never granted eQuip access (e.g. any member of the
        // Azure Workforce tenant). The rule is: every authenticated eQuip session
        // MUST carry the base 'user' role OR the standalone 'viewer' role.
        // Permission roles (admin, comptroller, finance, hr, service-member,
        // frontwindow) are additive on top of 'user'. The 'viewer' role is the
        // sole exception — it is a read-only standalone role that does not
        // require 'user'. A token with only a non-user, non-viewer permission
        // role is rejected so that Workforce misconfiguration (e.g. assigning
        // equip_comptroller without equip_user) fails closed rather than
        // granting partial access.
        const equipRoles = filterEquipRoles(user.roles);
        if (equipRoles.length === 0) {
          logger.warn('Rejecting login: identity has no eQuip application role', {
            component: 'AuthStore',
            username: user.username,
            roles: user.roles,
          });
          telemetry.track('auth.unauthorized.no_role', { username: user.username });
          get().clearAuth();
          throw new AuthorizationError();
        }
        const hasBaseAccess = equipRoles.includes('user') || equipRoles.includes('viewer');
        if (!hasBaseAccess) {
          logger.warn(
            'Rejecting login: identity is missing the required base user or viewer role',
            {
              component: 'AuthStore',
              username: user.username,
              roles: user.roles,
            },
          );
          telemetry.track('auth.unauthorized.missing_base_role', { username: user.username });
          get().clearAuth();
          throw new AuthorizationError();
        }

        set({
          user,
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
          refreshToken: tokens.refresh_token,
          isAuthenticated: true,
          isLoading: false,
          isDemoServiceMemberSession: isDemoSm,
        });

        // Successful auth clears the silent-SSO cooldown so subsequent page
        // refreshes can silent-reauth the user without dropping them to /login.
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem('silent_sso_last_fail');
          } catch {
            // Ignore storage access errors.
          }
        }

        scheduleTokenRefresh(tokens.access_token);
        logger.debug('Auth tokens set successfully', { component: 'AuthStore', roles: user.roles });
      } catch (error) {
        // Authorization rejection is not a system failure: the session was
        // already cleared at the gate. Propagate so callers can surface an
        // "access denied" message rather than a generic token error.
        if (error instanceof AuthorizationError) {
          throw error;
        }
        logger.error('Failed to set tokens', { component: 'AuthStore', error: String(error) });
        telemetry.trackError('auth.set_tokens.failed', error);
        get().clearAuth();
        throw error;
      }
    },

    refreshTokens: async (): Promise<boolean> => {
      if (refreshPromise) {
        return refreshPromise;
      }

      // Workforce BFF: the refresh token lives in an httpOnly cookie, not in JS.
      // Refresh via the BFF endpoint and update the in-memory access token.
      if (AUTH_BFF_ENABLED) {
        refreshPromise = (async () => {
          try {
            const tokens = await authService.refreshViaBff();
            if (!tokens) return false;
            await get().setTokens(tokens);
            return true;
          } catch {
            return false;
          } finally {
            refreshPromise = null;
          }
        })();
        return refreshPromise;
      }

      // Demo service-member sessions use a placeholder refresh token and are not
      // backed by Workforce. Clear the session and redirect to the login page silently
      // (no "session expired" modal — the user simply picks a new service member).
      const { isDemoServiceMemberSession } = get();
      if (isDemoServiceMemberSession) {
        get().clearAuth();
        if (typeof window !== 'undefined') {
          const from = window.location.pathname + window.location.search;
          if (from !== '/login') {
            try {
              localStorage.setItem('intended_route', from);
            } catch (storageError) {
              logger.warn('Failed to persist intended_route before redirect', {
                component: 'AuthStore',
                error: String(storageError),
              });
            }
          }
          window.dispatchEvent(new CustomEvent('auth:redirect-to-login', { detail: { from } }));
        }
        return false;
      }

      const { refreshToken } = get();
      if (!refreshToken) {
        return false;
      }

      // Validate refresh token expiry only when it is a JWT with an exp claim.
      // Azure Workforce refresh tokens are opaque (non-JWT), so getTokenExpiration returns
      // null for them. Treating null as "expired" would fire session-expired immediately
      // after every login. Only block on a decoded expiry that is provably in the past.
      try {
        const refreshTokenExpiry = getTokenExpiration(refreshToken);
        if (refreshTokenExpiry && refreshTokenExpiry.getTime() <= Date.now()) {
          logger.warn('Refresh token expired, clearing auth', { component: 'AuthStore' });
          telemetry.track('auth.refresh_token.expired');

          // Emit custom event for session expiration - App component will show modal
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
          }

          // Delay to allow modal to show before clearing auth
          setTimeout(() => {
            get().clearAuth();
          }, 100);
          return false;
        }
      } catch (error) {
        logger.warn('Could not decode refresh token expiry, proceeding with refresh attempt', {
          component: 'AuthStore',
          error: String(error),
        });
        // Non-fatal: let Workforce reject the token if it is actually invalid.
      }

      refreshPromise = (async () => {
        try {
          logger.debug('Attempting token refresh', { component: 'AuthStore' });
          const tokens = await authService.refreshToken(refreshToken);

          // Set tokens with validation
          await get().setTokens(tokens);

          // Fire-and-forget: re-run JIT provisioning so the DB record reflects
          // any profile or role changes that arrived with the refreshed token.
          const { accessToken: freshToken } = get();
          if (freshToken) {
            triggerJitProvisioning(freshToken);
          }

          logger.debug('Token refresh successful', { component: 'AuthStore' });
          return true;
        } catch (error) {
          if (error instanceof AuthorizationError) {
            // The refreshed token was issued but does not carry the required
            // eQuip roles (e.g. scope misconfiguration → Graph token with no
            // app roles). This is an authorization rejection, not a transient
            // failure. Redirect to login so the user can re-authenticate with
            // the correct scope instead of being left stranded with no token
            // (which causes a cascade of 401s on every API call).
            logger.warn(
              'Token refresh rejected: refreshed token missing required eQuip role — redirecting to login',
              {
                component: 'AuthStore',
              },
            );
            get().clearAuth();
            if (typeof window !== 'undefined') {
              const from = window.location.pathname + window.location.search;
              if (from !== '/login') {
                localStorage.setItem('intended_route', from);
              }
              window.dispatchEvent(new CustomEvent('auth:redirect-to-login', { detail: { from } }));
            }
          } else {
            logger.error('Token refresh failed', { component: 'AuthStore', error: String(error) });
            get().clearAuth();
          }
          return false;
        } finally {
          refreshPromise = null;
        }
      })();

      return refreshPromise;
    },

    initializeAuth: async () => {
      // Prevent concurrent auth initialization using timestamp-based deduplication
      // This approach is more reliable than boolean flags and self-cleans after timeout
      const AUTH_INIT_TIMEOUT = 30000; // 30 seconds
      const SILENT_SSO_COOLDOWN = 300000; // 5 minutes - prevent infinite redirect loops
      const now = Date.now();
      const lastInitStr = sessionStorage.getItem('auth_init_timestamp');

      // Clean up expired localStorage entries on init (prevents accumulation)
      try {
        const lastSilentFailStr = localStorage.getItem('silent_sso_last_fail');
        if (lastSilentFailStr) {
          const lastFail = parseInt(lastSilentFailStr, 10);
          if (now - lastFail > SILENT_SSO_COOLDOWN) {
            localStorage.removeItem('silent_sso_last_fail');
          }
        }
      } catch (error) {
        // Ignore localStorage errors (may be disabled/full)
        logger.warn('Failed to clean up localStorage', {
          component: 'AuthStore',
          error: String(error),
        });
      }

      // Skip silent SSO on callback page.
      // This guard must run before init dedupe checks to avoid callback-page loading deadlocks.
      // Do NOT skip on /login: refresh may briefly route there before silent SSO completes.
      if (typeof window !== 'undefined') {
        const pathname = window.location.pathname;
        if (pathname === '/auth/callback') {
          set({ isLoading: false });
          return;
        }

        // If login_initiated is set but we are NOT on the callback page, the
        // previous auth redirect never completed (e.g. OIDC authority was
        // misconfigured, browser was left on /oauth2/... and the user reloaded).
        // Clear stale artifacts so initializeAuth proceeds normally.
        if (localStorage.getItem('login_initiated') === 'true' && pathname !== '/auth/callback') {
          localStorage.removeItem('login_initiated');
          localStorage.removeItem('pkce_verifier');
          localStorage.removeItem('oauth_state');
          localStorage.removeItem('silent_login');
          sessionStorage.removeItem('auth_init_timestamp');
          logger.debug('Cleared stale login_initiated artifacts', {
            component: 'AuthStore',
            pathname,
          });
        }
      }

      // Workforce BFF: restore the session from the httpOnly cookie via the BFF
      // (a direct server call — no third-party-cookie iframe), which reliably
      // survives a browser refresh. On /auth/callback the AuthCallbackPage runs
      // the code exchange, so skip the restore there to avoid racing it.
      if (
        AUTH_BFF_ENABLED &&
        typeof window !== 'undefined' &&
        window.location.pathname !== '/auth/callback'
      ) {
        try {
          const tokens = await authService.getBffSession();
          if (tokens) {
            await get().setTokens(tokens);
          }
        } catch (error) {
          logger.warn('BFF session restore failed', {
            component: 'AuthStore',
            error: String(error),
          });
        }
        set({ isLoading: false });
        return;
      }

      if (lastInitStr) {
        const lastInit = parseInt(lastInitStr, 10);
        const timeSinceInit = now - lastInit;

        // If init was started recently, skip to prevent concurrent attempts
        if (timeSinceInit < AUTH_INIT_TIMEOUT) {
          const loginInProgress =
            localStorage.getItem('login_initiated') === 'true' ||
            Boolean(sessionStorage.getItem('token_exchange_in_progress'));

          logger.debug('Auth initialization already in progress or completed recently', {
            component: 'AuthStore',
            timeSinceInit,
            loginInProgress,
          });

          // Keep loading state while auth redirect/token exchange is in progress
          // to avoid flashing the login UI during page refresh.
          if (!loginInProgress) {
            set({ isLoading: false });
          }
          return;
        } else {
          // Clean up expired timestamp
          sessionStorage.removeItem('auth_init_timestamp');
        }
      }

      // Check if silent SSO was recently attempted and failed
      // Use localStorage to persist across tab/browser closes (prevents infinite redirect loops)
      const lastSilentFailStr = localStorage.getItem('silent_sso_last_fail');
      if (lastSilentFailStr) {
        const lastFail = parseInt(lastSilentFailStr, 10);
        if (now - lastFail < SILENT_SSO_COOLDOWN) {
          logger.debug('Silent SSO recently failed, skipping to prevent redirect loop', {
            component: 'AuthStore',
            timeSinceFail: now - lastFail,
          });
          set({ isLoading: false });
          return;
        }
      }

      // Attempt silent SSO on app load if not already authenticated
      const { isAuthenticated } = get();

      if (isAuthenticated) {
        set({ isLoading: false });
        return;
      }

      // Skip silent SSO in test mode (E2E testing)
      if (typeof window !== 'undefined' && sessionStorage.getItem('__test_mode__')) {
        logger.debug('Test mode detected, skipping silent SSO', { component: 'AuthStore' });
        set({ isLoading: false });
        return;
      }

      try {
        // Set timestamp to prevent concurrent auth attempts
        sessionStorage.setItem('auth_init_timestamp', now.toString());

        logger.debug('Attempting silent SSO (iframe)', { component: 'AuthStore' });
        telemetry.track('auth.silent_sso.started');

        // Use iframe-based silent reauth (prompt=none inside a hidden iframe).
        // This never causes a full-page redirect, so Azure Workforce errors (e.g.
        // AADSTS50058 - no session) are caught silently without showing an error
        // page to the user.
        const tokens = await authService.silentReauth();

        if (tokens) {
          await get().setTokens(tokens);
          logger.debug('Silent SSO succeeded', { component: 'AuthStore' });
          telemetry.track('auth.silent_sso.success');
        } else {
          // No active SSO session — fall through to the login page.
          logger.debug('Silent SSO: no active session, proceeding to login', {
            component: 'AuthStore',
          });
          telemetry.track('auth.silent_sso.unavailable', { error: 'no_session' });
          localStorage.setItem('silent_sso_last_fail', Date.now().toString());
          set({ isLoading: false });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.warn('Silent SSO attempt failed', {
          component: 'AuthStore',
          error: errorMessage,
        });
        telemetry.track('auth.silent_sso.network_error', { error: errorMessage });
        set({ isLoading: false });
      }
    },
  };
});

// Tokens are kept in-memory only to minimize XSS exposure.
// Session restoration uses silent SSO (prompt=none) on app load.

// Expose store to window for E2E testing
if (typeof window !== 'undefined') {
  (window as TestWindow).useAuthStore = useAuthStore;
}
